import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../api/endpoints.js';
import { Page } from '../../components/AppShell.jsx';
import {
  Badge, Button, Card, DefinitionList, Dialog, ErrorState, Field, Loading, Select, Textarea,
} from '../../components/ui.jsx';
import { toast } from '../../lib/toast.js';
import { date, humanise, phoneWithCountryCode } from '../../lib/format.js';
import { severityTone } from '../../lib/status.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { PERMISSIONS } from '../../lib/permissions.js';

/**
 * One report, with what it is about.
 *
 * The decision needs the evidence on the same page: a reported listing shows
 * the listing and who put it up; a reported chat shows who was in it and its
 * recent messages. Resolving acts — "suspend the listing" suspends it,
 * "suspend the account" suspends the person the report is against — so the
 * dialog says exactly what will happen before it does.
 */

const ACTIONS = {
  none: { label: 'No action needed', effect: 'The report is closed. Nothing else changes.' },
  warning: { label: 'Warned them', effect: 'The report is closed as a warning. Nothing is suspended — tell them yourself.' },
  listing_suspended: {
    label: 'Suspend the listing',
    effect: 'The listing comes down from search straight away and stops taking enquiries.',
    listingOnly: true,
  },
  user_suspended: {
    label: 'Suspend the account',
    effect: 'The account behind this report is signed out everywhere and cannot sign in until reinstated.',
  },
};

function Person({ person }) {
  if (!person) return <span className="muted">Unknown</span>;
  const href = person.kind === 'broker' || person.role === 'broker' ? null : `/users/${person.id}`;
  const name = person.name || 'Unnamed';
  return (
    <span>
      {href ? <Link to={href}>{name}</Link> : name}
      {person.phone ? <span className="muted mono"> · {phoneWithCountryCode(person.phone)}</span> : null}
      {person.status === 'suspended' ? <> {' '}<Badge status="suspended" /></> : null}
    </span>
  );
}

export default function ReportDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const canAct = can(PERMISSIONS.MODERATION_ACT);

  const [mode, setMode] = useState(null); // 'resolve' | 'dismiss'
  const [action, setAction] = useState('none');
  const [note, setNote] = useState('');

  const query = useQuery({ queryKey: ['admin-report', id], queryFn: () => api.moderation.report(id) });

  const resolve = useMutation({
    mutationFn: (body) => api.moderation.resolveReport(id, body),
    onSuccess: (_result, body) => {
      toast.success(body.dismiss ? 'Report dismissed.' : 'Report resolved.');
      setMode(null);
      setNote('');
      queryClient.invalidateQueries({ queryKey: ['admin-report', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-reports'] });
    },
    onError: (error) => toast.error(error.message || 'That decision could not be recorded.'),
  });

  if (query.isLoading) return <Loading label="Opening the report" />;
  if (query.isError) return <ErrorState error={query.error} onRetry={query.refetch} />;

  const report = query.data;
  const { target = {} } = report;
  const open = ['open', 'in_review'].includes(report.status);
  const isListing = target.type === 'broker_property';
  const offered = Object.entries(ACTIONS).filter(([, meta]) => !meta.listingOnly || isListing);

  return (
    <Page
      title={isListing ? `Report: ${target.title || 'listing'}` : 'Report: chat'}
      subtitle={`${humanise(report.category)} · reported ${date(report.createdAt, { withTime: true })}`}
      actions={
        <>
          <Button variant="ghost" onClick={() => navigate('/reports')}>
            Back to reports
          </Button>
          {canAct && open && (
            <>
              <Button variant="ghost" onClick={() => setMode('dismiss')}>
                Dismiss
              </Button>
              <Button variant="primary" onClick={() => setMode('resolve')}>
                Resolve
              </Button>
            </>
          )}
        </>
      }
    >
      <Card title="The report">
        <DefinitionList
          items={[
            { label: 'Status', value: <Badge status={report.status} /> },
            { label: 'Severity', value: <Badge tone={severityTone(report.severity)} label={report.severity} /> },
            { label: 'Reason', value: humanise(report.category) },
            { label: 'What they said', value: report.description },
            { label: 'Reported by', value: <Person person={report.reporter} /> },
            { label: 'Against', value: target.accused ? <Person person={target.accused} /> : null },
            { label: 'Decision', value: report.resolution?.action && humanise(report.resolution.action) },
            { label: 'Note', value: report.resolution?.note },
            { label: 'Decided', value: report.resolution?.resolvedAt && date(report.resolution.resolvedAt, { withTime: true }) },
          ]}
        />
      </Card>

      {isListing && (
        <Card
          title="The listing"
          actions={!target.missing && <Link to={`/inventory/${target.id}`}>Open the listing</Link>}
        >
          {target.missing ? (
            <p className="muted">This listing no longer exists.</p>
          ) : (
            <DefinitionList
              items={[
                { label: 'Title', value: target.title },
                { label: 'Area', value: target.locality },
                { label: 'Status', value: <Badge status={target.status} /> },
                { label: 'Agency', value: target.agency || 'Owner, no agency' },
                { label: 'Listed by', value: <Person person={target.accused} /> },
              ]}
            />
          )}
        </Card>
      )}

      {target.type === 'conversation' && (
        <Card
          title="The chat"
          subtitle="Shown to investigate this report. Its last messages, oldest first."
        >
          {target.missing ? (
            <p className="muted">This conversation no longer exists.</p>
          ) : (
            <>
              <DefinitionList
                items={target.participants.map((person) => ({
                  label: humanise(person.role),
                  value: (
                    <>
                      <Person person={person} />
                      {target.blockedBy?.includes(person.id) ? <span className="muted"> · blocked the chat</span> : null}
                    </>
                  ),
                }))}
              />
              <ol className="chat-log">
                {target.messages.map((message) => (
                  <li key={message.id} className="chat-log__item">
                    <div className="chat-log__meta">
                      <strong>{message.senderName || 'Unknown'}</strong>{' '}
                      <span className="muted">{date(message.createdAt, { withTime: true })}</span>
                    </div>
                    <div className="chat-log__body">{message.body || <span className="muted">(no text)</span>}</div>
                  </li>
                ))}
                {!target.messages.length && <li className="muted">No messages.</li>}
              </ol>
            </>
          )}
        </Card>
      )}

      <Dialog
        open={mode === 'resolve'}
        onClose={() => setMode(null)}
        title="Resolve this report"
        warning={ACTIONS[action]?.effect}
        footer={
          <>
            <Button variant="ghost" onClick={() => setMode(null)}>
              Cancel
            </Button>
            <Button
              variant={action.endsWith('suspended') ? 'danger' : 'primary'}
              loading={resolve.isPending}
              disabled={note.trim().length < 3}
              onClick={() => resolve.mutate({ action, note: note.trim() })}
            >
              {ACTIONS[action]?.label}
            </Button>
          </>
        }
      >
        <Field label="What to do">
          <Select value={action} onChange={(event) => setAction(event.target.value)}>
            {offered.map(([value, meta]) => (
              <option key={value} value={value}>
                {meta.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Note" hint="Required. Kept with the decision and in the audit log; used as the suspension reason.">
          <Textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Asked for a deposit before any viewing" />
        </Field>
      </Dialog>

      <Dialog
        open={mode === 'dismiss'}
        onClose={() => setMode(null)}
        title="Dismiss this report"
        warning="The report is closed with nothing changed — for reports that turn out to be unfounded."
        footer={
          <>
            <Button variant="ghost" onClick={() => setMode(null)}>
              Cancel
            </Button>
            <Button
              loading={resolve.isPending}
              disabled={note.trim().length < 3}
              onClick={() => resolve.mutate({ dismiss: true, note: note.trim() })}
            >
              Dismiss
            </Button>
          </>
        }
      >
        <Field label="Why" hint="Required. Kept with the report.">
          <Textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Listing checked with the owner; still available" />
        </Field>
      </Dialog>
    </Page>
  );
}
